import { useEffect, type RefObject } from "react";

/**
 * Close a small popover — a menu, a help tip — the two ways people expect:
 * pressing outside it, and Escape. `onClose` is told which, because only
 * Escape should send focus back to the trigger: after a press elsewhere, focus
 * belongs to whatever was pressed.
 *
 * `inside` is the trigger's element and `panel` the popover's own, when it is
 * drawn somewhere else in the page (see `popovers.tsx`). A press on either is
 * left alone: the trigger's own toggle owns that press, or the pointerdown
 * here would close the popover and the click reopen it.
 *
 * `pointerdown` rather than `click`, so a press that starts a drag or a text
 * selection elsewhere still dismisses, and so the popover is gone before
 * whatever was pressed reacts.
 */
export function useDismiss(
  open: boolean,
  onClose: (how: "outside" | "escape") => void,
  inside: RefObject<HTMLElement | null>,
  panel?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node) {
        if (inside.current?.contains(target) === true) return;
        if (panel?.current?.contains(target) === true) return;
      }
      onClose("outside");
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose("escape");
    };

    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, inside, panel]);
}
